import React from 'react';
import { Link } from 'react-router-dom';
import notFoundSvg from '../assets/images/not-found.svg';
import accessForbiddenSvg from '../assets/images/access-forbidden.svg';
import logoSvg from '../assets/images/logo.svg';

/**
 * Configuration for each error status code
 */
interface ErrorConfig {
  statusCode: number;
  title: string;
  message: string;
  illustration: string;
  reasons: string[];
  customMessage?: string;
}

const ERROR_CONFIGS: Record<number, ErrorConfig> = {
  404: {
    statusCode: 404,
    title: 'So Sorry!',
    message: 'The page you are looking for cannot be found',
    illustration: notFoundSvg,
    reasons: [
      'The address may have been typed incorrectly.',
      'It may be a broken or outdated link.',
    ],
  },
  403: {
    statusCode: 403,
    title: 'So Sorry!',
    message: 'You don\'t have access to this page',
    illustration: accessForbiddenSvg,
    reasons: [
      'You may not have the required permissions.',
      'The resource may be restricted for you.',
    ],
  },
};

/**
 * Common Error Page Layout Component
 * This component provides the shared structure for all error pages
 */
interface ErrorPageLayoutProps {
  children: React.ReactNode;
}

export const ErrorPageLayout: React.FC<ErrorPageLayoutProps> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top Logo Section */}
      <div className="flex w-full justify-center items-center p-8 gap-2">
        <img src={logoSvg} alt="Logo" className="w-10 h-10" />
        <span className="text-2xl font-bold text-gray-900">BlueGenAI</span>
      </div>

      {/* Main Content Section */}
      <div className="flex flex-1 items-center justify-center px-8 pb-16">
        {children}
      </div>
    </div>
  );
};

/**
 * Error Page Content Component
 * Renders the error-specific content within the common layout
 */
interface ErrorPageContentProps {
  config: ErrorConfig;
  redirectTo?: string;
  redirectLabel?: string;
}

const ErrorPageContent: React.FC<ErrorPageContentProps> = ({
  config,
  redirectTo = '/dashboard',
  redirectLabel = 'Go to Dashboard',
}) => {
  // Check for custom error message from session storage
  const errorMessage = sessionStorage.getItem('loginError');
  const displayMessage = errorMessage || config.message;
  
  // Clear the error message after reading
  if (errorMessage) {
    sessionStorage.removeItem('loginError');
  }

  return (
    <div className="max-w-6xl w-full flex gap-16 items-center">
      {/* Left Section - Text Content */}
      <div className="flex-1 flex flex-col">
        <h2 className="text-5xl font-bold text-gray-900 mb-4">
          {config.title}
        </h2>
        <h4 className="text-2xl font-bold text-gray-900 mb-6">
          {displayMessage}
        </h4>
        {config.reasons && config.reasons.length > 0 && (
          <div className="mb-8">
            <p className="text-lg font-semibold text-gray-900 mb-3">
              Possible Reasons
            </p>
            <ul className="list-none text-lg text-gray-700 space-y-2">
              {config.reasons.map((reason, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex gap-4">
          <Link
            to={redirectTo}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            {redirectLabel}
          </Link>
        </div>
      </div>

      {/* Right Section - Illustration */}
      <div className="flex-1 flex items-center justify-center">
        <img src={config.illustration} alt={config.title} className="w-full h-full" />
      </div>
    </div>
  );
};

/**
 * Main ErrorPage component that builds error pages based on status code
 */
interface ErrorPageProps {
  statusCode: number;
  redirectTo?: string;
  redirectLabel?: string;
  customMessage?: string;
}

const ErrorPage: React.FC<ErrorPageProps> = ({
  statusCode,
  redirectTo = '/dashboard',
  redirectLabel = 'Go to Dashboard',
  customMessage,
}) => {
  const config = ERROR_CONFIGS[statusCode];

  if (!config) {
    // Fallback for unknown status codes
    return (
      <ErrorPageLayout>
        <div className="max-w-6xl w-full text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            Error {statusCode}
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            An error occurred. Please try again later.
          </p>
          <Link
            to={redirectTo}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 inline-block"
          >
            {redirectLabel}
          </Link>
        </div>
      </ErrorPageLayout>
    );
  }

  // Override message if custom message is provided
  const finalConfig = customMessage
    ? { ...config, message: customMessage }
    : config;

  return (
    <ErrorPageLayout>
      <ErrorPageContent
        config={finalConfig}
        redirectTo={redirectTo}
        redirectLabel={redirectLabel}
      />
    </ErrorPageLayout>
  );
};

/**
 * 404 Not Found Error Page
 * Built by providing status code 404
 */
export const NotFoundErrorPage: React.FC = () => (
  <ErrorPage statusCode={404} />
);

/**
 * 403 Access Forbidden Error Page
 * Built by providing status code 403
 */
export const AccessForbiddenPage: React.FC = () => (
  <ErrorPage statusCode={403} />
);

export default ErrorPage;